import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { ToastContainer } from 'react-toastify';
import { HomeIcon, UsersIcon, UserGroupIcon, OfficeBuildingIcon, DocumentTextIcon, DocumentReportIcon, CogIcon, XIcon, MenuIcon, ShieldCheckIcon, BookOpenIcon } from '@heroicons/react/outline';
import Footer from "../../components/Footer";
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from "react";
import { hasRole } from "../../shared/Utils";
import { bindActionCreators } from "redux";
import { onLogoutRequested } from '../../actions/authAction';
import { stopImpersonation } from '../../actions/impersonateAction';
import { connect } from "react-redux";

const Dashboard = ({ user, onLogoutRequested: logoutUser, stopImpersonation: exitImpersonation, impersonatedUser, isImpersonating }) => {
    const navigate = useNavigate();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const activeUser = isImpersonating ? impersonatedUser : user;
    const leaderCells = useMemo(() => activeUser?.cells || [], [activeUser]);
    const cellStorageKey = activeUser?.id ? `activeCellId:${activeUser.id}` : null;

    // Leaders can lead several cells; remember which one they're working on.
    const [selectedCellId, setSelectedCellId] = useState(null);
    useEffect(() => {
        const stored = cellStorageKey ? Number(localStorage.getItem(cellStorageKey)) : null;
        setSelectedCellId(stored || null);
    }, [cellStorageKey]);

    const activeCell = leaderCells.find((c) => c.id === selectedCellId) || leaderCells[0] || null;

    const handleSelectCell = (event) => {
        const id = Number(event.target.value);
        setSelectedCellId(id);
        if (cellStorageKey) localStorage.setItem(cellStorageKey, String(id));
    };

    if (!user || !user.id) {
        navigate('/login');
        return null;
    }

    const name = user?.name;

    const logout = () => {
        logoutUser();
    };

    const navItems = [
        { to: '/dashboard/', label: 'Home', icon: HomeIcon, roles: [1, 2, 3], end: true },
        { to: '/dashboard/my-cells', label: 'My Cells', icon: OfficeBuildingIcon, roles: [1] },
        { to: '/dashboard/cells', label: 'Cells', icon: OfficeBuildingIcon, roles: [2] },
        { to: '/dashboard/cellmembers', label: 'Cell Members', icon: UserGroupIcon, roles: [1] },
        { to: '/dashboard/guests', label: 'Guests', icon: UsersIcon, roles: [1] },
        { to: '/dashboard/reports', label: 'Reports', icon: DocumentReportIcon, roles: [1] },
        { to: '/dashboard/users', label: 'Cell Leaders', icon: UsersIcon, roles: [2] },
        { to: '/dashboard/admins', label: 'Admins', icon: ShieldCheckIcon, roles: [3] },
        { to: '/dashboard/admin/reports', label: 'Admin Reports', icon: DocumentTextIcon, roles: [2] },
        { to: '/dashboard/verse', label: 'Verse of the Day', icon: BookOpenIcon, roles: [1, 2, 3] },
        { to: '/dashboard/settings', label: 'Settings', icon: CogIcon, roles: [1, 2, 3] },
    ];

    const visibleNavItems = navItems.filter((item) => hasRole(activeUser, ...item.roles));

    const navLinkClassName = ({ isActive }) => (
        `group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200 ${
            isActive
                ? 'bg-emerald-50 text-emerald-700 shadow-sm'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
        }`
    );

    const renderNavLinks = (onClick) => (
        visibleNavItems.map((item) => {
            const Icon = item.icon;
            return (
                <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    className={navLinkClassName}
                    onClick={onClick}
                >
                    <Icon className="h-5 w-5" />
                    <span>{item.label}</span>
                </NavLink>
            );
        })
    );

    return (
        <div className="min-h-screen bg-slate-100">
            <ToastContainer />
            <div className="flex min-h-screen">
                <aside className="hidden lg:sticky lg:top-0 lg:flex lg:h-screen lg:w-80 lg:flex-col lg:border-r lg:border-gray-200 lg:bg-white">
                    <div className="flex h-screen min-h-0 flex-col px-6 py-6">
                        <div className="mb-8">
                            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-emerald-600">ANFGC</p>
                            <h1 className="mt-2 text-2xl font-semibold text-gray-900">Cell Report</h1>
                        </div>

                        <div className="min-h-0 flex-1 overflow-y-auto pr-1">
                            <nav className="space-y-2">
                                {renderNavLinks(undefined)}
                            </nav>
                        </div>

                        <div className="mt-4 shrink-0 border-t border-gray-100 pt-4">
                            <button
                                className="flex w-full items-center justify-center rounded-lg bg-red-600 px-3 py-2.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-red-700"
                                onClick={logout}
                            >
                                Logout
                            </button>
                        </div>
                    </div>
                </aside>

                <div className="flex min-h-screen min-w-0 flex-1 flex-col">
                    <header className="sticky top-0 z-40 border-b border-gray-200 bg-white/80 backdrop-blur">
                        <div className="flex h-16 w-full items-center justify-between px-4 sm:px-6 lg:px-8">
                            <button
                                type="button"
                                className="inline-flex items-center justify-center rounded-md p-2 text-gray-600 hover:bg-gray-100 lg:hidden"
                                onClick={() => setIsMobileMenuOpen(true)}
                            >
                                <MenuIcon className="h-6 w-6" />
                            </button>

                            <div className="hidden lg:block">
                                <p className="text-sm text-gray-500">Dashboard</p>
                            </div>

                            <div className="flex items-center gap-3">
                                {leaderCells.length > 1 && activeCell && (
                                    <label className="flex items-center gap-2 text-sm text-gray-600">
                                        <span className="hidden sm:inline">Cell:</span>
                                        <select
                                            aria-label="Current cell"
                                            value={activeCell.id}
                                            onChange={handleSelectCell}
                                            className="rounded-md border-gray-300 py-1 text-sm font-medium text-gray-800 shadow-sm focus:border-emerald-300 focus:ring focus:ring-emerald-200 focus:ring-opacity-50"
                                        >
                                            {leaderCells.map((cell) => (
                                                <option key={cell.id} value={cell.id}>{cell.name}</option>
                                            ))}
                                        </select>
                                    </label>
                                )}
                                <div className="rounded-full bg-gray-100 px-4 py-1.5 text-sm font-medium text-gray-700">
                                    {name}
                                </div>
                            </div>
                        </div>
                    </header>

                    {isImpersonating && (
                        <div className="bg-amber-500 text-white px-4 py-2 flex items-center justify-between sm:px-6 lg:px-8">
                            <span className="text-sm font-medium">
                                Viewing as <strong>{impersonatedUser?.name}</strong>
                            </span>
                            <button
                                onClick={() => exitImpersonation()}
                                className="bg-white text-amber-600 font-semibold text-xs px-3 py-1 rounded-full hover:bg-amber-50 transition-colors"
                            >
                                Exit
                            </button>
                        </div>
                    )}

                    <main className="flex-1">
                        <section className="w-full px-4 pb-8 pt-4 sm:px-6 lg:px-8">
                            <Outlet context={{ activeUser: isImpersonating ? impersonatedUser : null, activeCell }} />
                        </section>
                    </main>

                    <Footer />
                </div>
            </div>

            {isMobileMenuOpen && (
                <div className="fixed inset-0 z-50 lg:hidden">
                    <div
                        className="absolute inset-0 bg-black/40"
                        onClick={() => setIsMobileMenuOpen(false)}
                    />
                    <aside className="absolute left-0 top-0 h-full w-80 max-w-[85vw] bg-white shadow-2xl">
                        <div className="flex h-full min-h-0 flex-col px-5 py-5">
                            <div className="mb-6 flex items-center justify-between">
                                <h2 className="text-xl font-semibold text-gray-900">Cell Report</h2>
                                <button
                                    type="button"
                                    className="rounded-md p-2 text-gray-600 hover:bg-gray-100"
                                    onClick={() => setIsMobileMenuOpen(false)}
                                >
                                    <XIcon className="h-5 w-5" />
                                </button>
                            </div>

                            <div className="min-h-0 flex-1 overflow-y-auto pr-1">
                                <nav className="space-y-2">
                                    {renderNavLinks(() => setIsMobileMenuOpen(false))}
                                </nav>
                            </div>

                            <div className="mt-4 shrink-0 border-t border-gray-100 pt-4">
                                <button
                                    className="flex w-full items-center justify-center rounded-lg bg-red-600 px-3 py-2.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-red-700"
                                    onClick={logout}
                                >
                                    Logout
                                </button>
                            </div>
                        </div>
                    </aside>
                </div>
            )}
        </div>
    );
}

Dashboard.propTypes = {
    user: PropTypes.object.isRequired,
}

Dashboard.defaultProps = {
    user: {}
}

const mapStateToProps = (state) => ({
    impersonatedUser: state.impersonate.impersonatedUser,
    isImpersonating: state.impersonate.isImpersonating,
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
    onLogoutRequested,
    stopImpersonation,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(Dashboard);
